import { graphqlClient } from '@/graphql/graphql-client';
import { toOrgContractDocumentsRequest } from '@/graphql/queries';
import {
  INSERT_ORG_CONTRACT_DOCUMENT_MUTATION,
  UPDATE_ORG_CONTRACT_DOCUMENT_MUTATION,
  DELETE_ORG_CONTRACT_DOCUMENT_MUTATION,
} from '@/graphql/mutations';
import type {
  GetOrgContractDocumentsResponse,
  OrgContractDocumentInsertInput,
  OrgContractDocumentUpdateInput,
  MutateOrgContractDocumentResponse,
  PaginationParams,
} from '../types/contract-document.types';

// ---------------------------------------------------------------------------
// Mutation response wrappers
// ---------------------------------------------------------------------------

export interface InsertOrgContractDocumentResponse {
  insertOrgContractDocument: MutateOrgContractDocumentResponse & { itemId: string };
}

export interface UpdateOrgContractDocumentResponse {
  updateOrgContractDocument: MutateOrgContractDocumentResponse;
}

export interface DeleteOrgContractDocumentResponse {
  deleteOrgContractDocument: MutateOrgContractDocumentResponse;
}

// ---------------------------------------------------------------------------
// GET
// ---------------------------------------------------------------------------

export const getContractDocuments = async (
  params: PaginationParams
): Promise<GetOrgContractDocumentsResponse['getOrgContractDocuments']> => {
  const { document, variables } = toOrgContractDocumentsRequest(params);
  const pageNo = variables.pageNo;
  const pageSize = variables.pageSize;

  try {
    const response = await graphqlClient.query({
      query: document,
      variables,
    });

    const result = response?.getOrgContractDocuments ?? null;

    if (result) {
      return {
        items: result.items || [],
        totalCount: result.totalCount || 0,
        hasNextPage: result.hasNextPage || false,
        hasPreviousPage: result.hasPreviousPage || false,
        pageSize: result.pageSize || pageSize,
        pageNo: result.pageNo || pageNo,
        totalPages: result.totalPages || 1,
      };
    }

    return {
      items: [],
      totalCount: 0,
      hasNextPage: false,
      hasPreviousPage: false,
      pageSize,
      pageNo,
      totalPages: 0,
    };
  } catch (error) {
    console.error('Error fetching contract documents:', error);
    return {
      items: [],
      totalCount: 0,
      hasNextPage: false,
      hasPreviousPage: false,
      pageSize,
      pageNo,
      totalPages: 0,
    };
  }
};

// Fetch a single contract document by ItemId (used when callers know the
// exact id and just want one row's worth of fields, e.g. to check Status).
export const getContractDocumentById = async (itemId: string) => {
  if (!itemId) return null;
  const result = await getContractDocuments({
    where: { ItemId: { eq: itemId } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  return result.items[0] ?? null;
};

// ---------------------------------------------------------------------------
// CREATE
// ---------------------------------------------------------------------------

export const createContractDocument = async (
  input: OrgContractDocumentInsertInput
): Promise<InsertOrgContractDocumentResponse> => {
  const inputWithDefaults: OrgContractDocumentInsertInput = {
    ...input,
    IsArchived: input.IsArchived ?? false,
    HasSentForApproval: input.HasSentForApproval ?? false,
    IsApproved: input.IsApproved ?? false,
    Reviewers: input.Reviewers ?? [],
  };

  const response = await graphqlClient.request(INSERT_ORG_CONTRACT_DOCUMENT_MUTATION, inputWithDefaults);

  const result = response.insertOrgContractDocument;
  if (!result?.itemId) {
    throw new Error('No itemId returned from insert');
  }
  return { insertOrgContractDocument: { ...result, itemId: result.itemId } };
};

// ---------------------------------------------------------------------------
// UPDATE
// ---------------------------------------------------------------------------

export const updateContractDocument = async (
  itemId: string,
  input: OrgContractDocumentUpdateInput
): Promise<UpdateOrgContractDocumentResponse> => {
  const response = await graphqlClient.request(UPDATE_ORG_CONTRACT_DOCUMENT_MUTATION, { ItemId: itemId, ...input });

  return response;
};

export const updateContractDocumentSigDocumentId = async (
  draftId: string,
  sigDocumentId: string,
  sigDocumentUrl?: string
): Promise<UpdateOrgContractDocumentResponse> =>
  updateContractDocument(draftId, {
    SigDocumentId: sigDocumentId,
    ...(sigDocumentUrl ? { SigDocumentUrl: sigDocumentUrl } : {}),
  });

// ---------------------------------------------------------------------------
// DELETE
// ---------------------------------------------------------------------------

export const deleteContractDocument = async (
  itemId: string,
  isHardDelete = false
): Promise<DeleteOrgContractDocumentResponse> => {
  const response = await graphqlClient.request(DELETE_ORG_CONTRACT_DOCUMENT_MUTATION, { ItemId: itemId, isHardDelete });

  return response;
};
